<template>
  <header class="pt-bar">
    <div class="pt-bar-inner">
      <router-link :to="home" class="pt-brand" aria-label="Portal home">
        <BrandMark :size="34" on="dark" />
        <span class="pt-wordmark">
          <span class="pt-wm-top">Municipal</span>
          <span class="pt-wm-bottom">Collectionz</span>
        </span>
        <span class="pt-role">{{ roleLabel }}</span>
      </router-link>

      <nav class="pt-links" :class="{ open: menuOpen }" aria-label="Portal">
        <router-link
          v-for="l in links"
          :key="l.to"
          :to="l.to"
          class="pt-link"
          :class="{ on: isActive(l) }"
          @click.native="menuOpen = false"
        >
          <SiteIcon :name="l.icon" :size="17" />
          <span>{{ l.label }}</span>
        </router-link>
      </nav>

      <div class="pt-right">
        <div class="pt-user" @click.stop="userOpen = !userOpen">
          <span class="pt-avatar">{{ initials }}</span>
          <span class="pt-user-name">{{ name }}</span>
          <SiteIcon name="chevronDown" :size="16" />
          <div v-if="userOpen" class="pt-user-menu" @click.stop>
            <div class="pt-user-head">
              <div class="pt-user-full">{{ name }}</div>
              <div class="pt-user-email">{{ email }}</div>
            </div>
            <router-link :to="settingsRoute" class="pt-menu-item" @click.native="userOpen = false">
              <SiteIcon name="gear" :size="17" /> Settings
            </router-link>
            <router-link to="/" class="pt-menu-item" @click.native="userOpen = false">
              <SiteIcon name="globe" :size="17" /> Public website
            </router-link>
            <button type="button" class="pt-menu-item danger" @click="logout">
              <SiteIcon name="logout" :size="17" /> Sign out
            </button>
          </div>
        </div>
        <button type="button" class="pt-burger" aria-label="Menu" @click.stop="menuOpen = !menuOpen">
          <SiteIcon :name="menuOpen ? 'close' : 'menu'" :size="22" />
        </button>
      </div>
    </div>
  </header>
</template>

<script>
// Navy top bar shared by every staff and admin page.
// <PortalTopBar role="staff" />  or  <PortalTopBar role="admin" />
import { auth, db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import BrandMark from "@/components/BrandMark.vue";
import SiteIcon from "@/components/SiteIcon.vue";

const LINKS = {
  staff: [
    { to: "/staff-dashboard", label: "Dashboard", icon: "home", exact: true },
    { to: "/staff-leads", label: "Leads", icon: "target" },
    { to: "/staff-onboard-customer", label: "Onboard a city", icon: "userPlus" },
    { to: "/staff/templates", label: "Templates", icon: "mail" },
    { to: "/staff/blog", label: "Blog", icon: "news" },
    { to: "/staff-payments", label: "Payments", icon: "card" },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: "home", exact: true },
    { to: "/admin/revenue", label: "Revenue", icon: "chart" },
    { to: "/manage-app-progress", label: "Rollouts", icon: "flag" },
    { to: "/admin/templates", label: "Templates", icon: "mail" },
    { to: "/staff/blog", label: "Blog", icon: "news" },
    { to: "/admin/products", label: "Products", icon: "grid" },
  ],
  customer: [
    { to: "/dashboard", label: "Overview", icon: "home", exact: true },
    { to: "/dashboard/imports", label: "Upload files", icon: "upload" },
    { to: "/customer-settings", label: "Settings", icon: "gear" },
  ],
};

export default {
  name: "PortalTopBar",
  components: { BrandMark, SiteIcon },
  props: {
    role: { type: String, default: "staff" },
  },
  data() {
    return { name: "", email: "", menuOpen: false, userOpen: false };
  },
  computed: {
    links() {
      return LINKS[this.role] || LINKS.staff;
    },
    home() {
      return { admin: "/admin", customer: "/dashboard" }[this.role] || "/staff-dashboard";
    },
    settingsRoute() {
      return { admin: "/admin-settings", customer: "/customer-settings" }[this.role] || "/staff-manage-settings";
    },
    roleLabel() {
      return { admin: "ADMIN", customer: "CITY" }[this.role] || "STAFF";
    },
    initials() {
      const src = (this.name || this.email || "?").trim();
      return src.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
    },
  },
  mounted() {
    document.addEventListener("click", this.closeMenus);
    this._unsubAuth = auth.onAuthStateChanged(async (u) => {
      if (!u) return;
      this.email = u.email || "";
      this.name = u.displayName || "";
      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (snap.exists()) {
          const d = snap.data();
          this.name = `${d.firstName || ""} ${d.lastName || ""}`.trim() || d.fullName || d.displayName || this.name;
        }
      } catch (e) {
        /* keep the auth name */
      }
      if (!this.name) this.name = this.email.replace(/@.*/, "");
    });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeMenus);
    if (this._unsubAuth) this._unsubAuth();
  },
  methods: {
    closeMenus() {
      this.userOpen = false;
      this.menuOpen = false;
    },
    isActive(l) {
      const p = this.$route.path;
      return l.exact ? p === l.to : p === l.to || p.startsWith(l.to + "/");
    },
    async logout() {
      this.userOpen = false;
      await auth.signOut();
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.pt-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #0e2236;
  color: #f3ede0;
  box-shadow: 0 2px 14px rgba(11, 29, 46, 0.25);
  font-family: "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.pt-bar-inner {
  display: flex;
  align-items: center;
  gap: 22px;
  max-width: 1280px;
  height: 64px;
  margin: 0 auto;
  padding: 0 24px;
}

.pt-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  color: #f3ede0;
  text-decoration: none;
}

.pt-wordmark {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.pt-wm-top {
  font-family: "Fraunces", Georgia, serif;
  font-size: 1.1rem;
  font-weight: 700;
}

.pt-wm-bottom {
  margin-top: 2px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #7cc4a5;
}

.pt-role {
  padding: 3px 8px;
  border: 1px solid rgba(124, 196, 165, 0.5);
  border-radius: 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: #7cc4a5;
}

.pt-links {
  display: flex;
  align-items: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.pt-links::-webkit-scrollbar {
  display: none;
}

.pt-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  color: rgba(243, 237, 224, 0.72);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}

.pt-link:hover {
  background: rgba(243, 237, 224, 0.08);
  color: #fff;
}

.pt-link.on {
  background: rgba(124, 196, 165, 0.16);
  color: #fff;
}

.pt-link.on .site-icon {
  color: #7cc4a5;
}

.pt-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.pt-user {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 8px 0 4px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
}

.pt-user:hover {
  background: rgba(243, 237, 224, 0.08);
}

.pt-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #1e6b52;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Fraunces", Georgia, serif;
  font-size: 0.85rem;
  font-weight: 700;
}

.pt-user-name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
}

.pt-user-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  padding: 6px;
  border-radius: 10px;
  background: #fffdf8;
  border: 1px solid rgba(14, 34, 54, 0.12);
  box-shadow: 0 18px 40px rgba(11, 29, 46, 0.25);
  color: #0e2236;
  cursor: default;
}

.pt-user-head {
  padding: 10px 10px 12px;
  margin-bottom: 4px;
  border-bottom: 1px solid rgba(14, 34, 54, 0.1);
}

.pt-user-full {
  font-weight: 700;
  font-size: 14px;
}

.pt-user-email {
  font-size: 12.5px;
  color: #66727f;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pt-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #0e2236;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.pt-menu-item:hover {
  background: #f6f1e6;
}

.pt-menu-item.danger {
  color: #8e2f1c;
}

.pt-burger {
  display: none;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(243, 237, 224, 0.2);
  border-radius: 8px;
  background: transparent;
  color: #f3ede0;
  cursor: pointer;
}

@media (max-width: 1080px) {
  .pt-user-name {
    display: none;
  }
}

@media (max-width: 900px) {
  .pt-burger {
    display: flex;
  }
  .pt-links {
    position: absolute;
    top: 64px;
    left: 0;
    right: 0;
    display: none;
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
    padding: 8px 12px 14px;
    background: #0e2236;
    border-top: 1px solid rgba(243, 237, 224, 0.1);
    box-shadow: 0 16px 30px rgba(11, 29, 46, 0.3);
  }
  .pt-links.open {
    display: flex;
  }
  .pt-link {
    height: 46px;
  }
}

@media (max-width: 520px) {
  .pt-bar-inner {
    padding: 0 12px;
    gap: 10px;
  }
  .pt-role {
    display: none;
  }
}
</style>